import type { QueryClient } from "@tanstack/react-query";
import z from "zod";

import { appQueriesStore } from "@/app/api";
import { portalUserSchema } from "@/app/models";
import { parseResponseData, privateAPI, publicAPI } from "@/shared/lib";
import type { ServiceResponse } from "@/shared/lib/axios";

const authResponseSchema = z.object({
  accessToken: z.string(),
  preferredLanguage: portalUserSchema.shape.preferredLanguage,
});

interface LoginRequest {
  email: string;
  password: string;
}

export const login = {
  mutation: async (params: LoginRequest) => {
    const response = await publicAPI.post("/login", params);

    return parseResponseData(response, authResponseSchema);
  },
};

export const logout = async () => {
  return await privateAPI.post("/logout");
};

export const setPassword = {
  mutation: async ({
    password,
    passwordConfirmation,
    token,
  }: {
    password: string;
    passwordConfirmation: string;
    token?: string | null;
  }) => {
    const { data } = await publicAPI.put<
      ServiceResponse<{ patientId: string }>
    >("/patients/set-password", {
      password,
      passwordConfirmation,
      token,
    });

    return data.data;
  },
};

export const setLevelsRxPassword = {
  mutation: async ({
    password,
    passwordConfirmation,
  }: {
    password: string;
    passwordConfirmation: string;
  }) => {
    const { data } = await privateAPI.put<ServiceResponse<void>>(
      "/patients/set-levels-rx-password",
      {
        password,
        passwordConfirmation,
      },
    );

    return data.data;
  },
  invalidates: (queryClient: QueryClient) => {
    void queryClient.invalidateQueries({
      queryKey: appQueriesStore.user.queryKey,
    });
  },
};

export const forgotPassword = {
  mutation: async ({ email }: { email: string }) => {
    await publicAPI.post("/patients/forgot-password", { email });
  },
};
